import { useEffect, useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { inr, inrFull } from "@/lib/format";

const STAMP_PCT = 0.049;
const REG_PCT = 0.01;
const LEGAL_PCT = 0.005;
const LEGAL_FLAT = 50000;

const SCENARIOS = [
  { id: "conservative", label: "Conservative", rate: 8 },
  { id: "expected", label: "Expected", rate: 12 },
  { id: "optimistic", label: "Optimistic", rate: 18 },
];

function Field({ label, display, value, min, max, step, onChange, testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          {label}
        </span>
        <span className="font-mono text-sm font-bold" data-testid={`${testid}-value`}>
          {display}
        </span>
      </div>
      <Slider
        className="mt-3"
        value={[value]}
        onValueChange={(v) => onChange(v[0])}
        min={min}
        max={max}
        step={step}
        data-testid={testid}
      />
    </div>
  );
}

export default function Calculator({ open, onOpenChange, seed }) {
  const [area, setArea] = useState(500);
  const [pps, setPps] = useState(6800);
  const [rate, setRate] = useState(12);

  useEffect(() => {
    if (seed) {
      setArea(Math.min(10000, Math.max(100, seed.area)));
      setPps(Math.min(15000, Math.max(2000, seed.pps)));
    }
  }, [seed]);

  const calc = useMemo(() => {
    const base = area * pps;
    const stamp = base * STAMP_PCT;
    const reg = base * REG_PCT;
    const legal = base * LEGAL_PCT + LEGAL_FLAT;
    const total = base + stamp + reg + legal;
    const years = [1, 2, 3, 4, 5].map((n) => {
      const value = Math.round(base * Math.pow(1 + rate / 100, n));
      return {
        year: `YR ${n}`,
        value,
        growthPct: +(((value - base) / base) * 100).toFixed(1),
        increase: value - base,
        low: Math.round(base * Math.pow(1 + SCENARIOS[0].rate / 100, n)),
        high: Math.round(base * Math.pow(1 + SCENARIOS[2].rate / 100, n)),
      };
    });
    return {
      base,
      stamp,
      reg,
      legal,
      total,
      years,
      finalValue: years[4].value,
      gain: years[4].value - total,
    };
  }, [area, pps, rate]);

  const activeScenario = SCENARIOS.find((s) => s.rate === rate)?.id || null;

  const rows = [
    { label: "Base land cost", value: calc.base },
    { label: "Stamp duty @ 4.9%", value: calc.stamp },
    { label: "Registration @ 1.0%", value: calc.reg },
    { label: "Legal & premium (0.5% + ₹50K)", value: calc.legal },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto border-l border-border p-0 sm:max-w-xl"
        data-testid="calculator-panel"
      >
        <SheetHeader className="border-b border-border p-6 text-left">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
            ROI Engine — Live
          </p>
          <SheetTitle className="font-display text-2xl font-medium uppercase tracking-tight">
            Run the numbers
          </SheetTitle>
          <SheetDescription className="text-xs">
            Every slider recalculates instantly. Duties as per the Gujarat schedule.
          </SheetDescription>
          {seed?.name && (
            <p
              className="!mt-3 inline-block border border-accent/40 bg-accent/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-accent"
              data-testid="calc-prefill-note"
            >
              Prefilled — {seed.name}
            </p>
          )}
        </SheetHeader>

        <div className="space-y-8 p-6" data-testid="calculator-inputs">
          <Field
            label="Plot Area"
            display={`${area.toLocaleString("en-IN")} SQ YD`}
            value={area}
            min={100}
            max={10000}
            step={50}
            onChange={setArea}
            testid="calc-area-slider"
          />
          <Field
            label="Base Price / SQ YD"
            display={inrFull(pps)}
            value={pps}
            min={2000}
            max={15000}
            step={100}
            onChange={setPps}
            testid="calc-price-slider"
          />
          <Field
            label="Expected Annual Appreciation"
            display={`${rate}% P.A.`}
            value={rate}
            min={4}
            max={22}
            step={0.5}
            onChange={setRate}
            testid="calc-rate-slider"
          />
          <div>
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Growth Scenario
              </span>
              {!activeScenario && (
                <span
                  className="border border-ember/40 bg-ember/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ember"
                  data-testid="scenario-custom-badge"
                >
                  Custom {rate}%
                </span>
              )}
            </div>
            <div
              className="mt-3 grid grid-cols-3 gap-px border border-border bg-border"
              data-testid="scenario-toggle"
            >
              {SCENARIOS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setRate(s.rate)}
                  className={`px-3 py-2.5 text-left transition-colors duration-200 ${
                    activeScenario === s.id
                      ? "bg-primary text-primary-foreground"
                      : "bg-background text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`scenario-${s.id}`}
                >
                  <span className="block font-mono text-[10px] uppercase tracking-[0.15em]">
                    {s.label}
                  </span>
                  <span className="mt-0.5 block font-mono text-xs font-bold">
                    {s.rate}% P.A.
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-border p-6" data-testid="calculator-breakdown">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            Acquisition Breakdown
          </p>
          <div className="mt-4">
            {rows.map((r) => (
              <div
                key={r.label}
                className="flex items-baseline justify-between border-b border-dashed border-border py-2.5 text-sm"
              >
                <span className="text-muted-foreground">{r.label}</span>
                <span className="font-mono font-bold">{inrFull(r.value)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em]">
              Total Acquisition
            </span>
            <span
              className="font-display text-2xl font-extrabold tracking-tight"
              data-testid="calc-total-cost"
            >
              {inr(calc.total)}
            </span>
          </div>
        </div>

        <div className="border-t border-border p-6" data-testid="calculator-chart">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              5-Year Projected Valuation
            </p>
            <span className="flex items-center gap-1.5 font-mono text-xs font-bold text-accent">
              <TrendingUp className="h-3.5 w-3.5" />+{inr(calc.gain)}
            </span>
          </div>
          <div className="mt-5 h-[210px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={calc.years}
                margin={{ top: 18, right: 0, left: 0, bottom: 0 }}
                barGap={-40}
              >
                <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="year"
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: 10,
                    fontFamily: '"Space Mono", monospace',
                    fill: "hsl(var(--muted-foreground))",
                  }}
                />
                <YAxis hide domain={[0, "dataMax * 1.12"]} />
                <Tooltip
                  cursor={{ fill: "hsl(var(--muted))" }}
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 0,
                    fontFamily: '"Space Mono", monospace',
                    fontSize: 12,
                  }}
                  formatter={(v, name) => [inr(v), name]}
                />
                <ReferenceLine
                  y={calc.total}
                  stroke="#EA580C"
                  strokeDasharray="4 4"
                />
                <Bar
                  dataKey="high"
                  name="Optimistic (18%)"
                  barSize={40}
                  fill="transparent"
                  stroke="#10B981"
                  strokeOpacity={0.35}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey="low"
                  name="Conservative (8%)"
                  barSize={40}
                  fill="transparent"
                  stroke="#64748B"
                  strokeOpacity={0.5}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey="value"
                  name="Active scenario"
                  barSize={40}
                  radius={[0, 0, 0, 0]}
                >
                  {calc.years.map((_, i) => (
                    <Cell key={i} fill={i === 4 ? "#EA580C" : "#10B981"} />
                  ))}
                  <LabelList
                    dataKey="growthPct"
                    position="top"
                    formatter={(v) => `+${v}%`}
                    style={{
                      fontSize: 9,
                      fontFamily: '"Space Mono", monospace',
                      fill: "hsl(var(--muted-foreground))",
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 border border-[#10B981]/50" />
              Optimistic
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 border border-[#64748B]/70" />
              Conservative
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 border border-dashed border-[#EA580C]" />
              Total outlay
            </span>
          </div>
        </div>

        <div className="border-t border-border p-6" data-testid="yearly-breakdown">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            Yearly Breakdown
          </p>
          <div className="mt-4 space-y-2">
            {calc.years.map((y, i) => {
              const isFinal = i === 4;
              return (
                <div
                  key={y.year}
                  className={`flex items-center gap-4 border px-4 py-3 ${
                    isFinal
                      ? "border-ember/50 bg-ember/10"
                      : "border-border bg-background"
                  }`}
                  data-testid={`year-row-${i + 1}`}
                >
                  <span className="w-12 shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Year {i + 1}
                  </span>
                  <span
                    className={`min-w-0 flex-1 truncate font-mono font-bold ${
                      isFinal ? "text-lg text-ember" : "text-sm"
                    }`}
                    data-testid={isFinal ? "year5-final-value" : undefined}
                  >
                    {inrFull(y.value)}
                  </span>
                  <span
                    className={`shrink-0 border px-2 py-0.5 font-mono text-[10px] font-bold ${
                      isFinal
                        ? "border-ember/50 text-ember"
                        : "border-accent/40 text-accent"
                    }`}
                  >
                    +{y.growthPct}%
                  </span>
                  <span className="hidden w-24 shrink-0 text-right font-mono text-xs text-muted-foreground sm:block">
                    +{inr(y.increase)}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Growth vs base land cost · last column is total value added
          </p>
        </div>

        <div className="border-t border-border bg-secondary/50 p-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Value in Year 5
              </p>
              <p
                className="mt-1 font-display text-2xl font-extrabold tracking-tight"
                data-testid="calc-final-value"
              >
                {inr(calc.finalValue)}
              </p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Net gain vs outlay
              </p>
              <p
                className="mt-1 font-display text-2xl font-extrabold tracking-tight text-accent"
                data-testid="calc-net-gain"
              >
                +{inr(calc.gain)}
              </p>
            </div>
          </div>
          <p className="mt-5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            Projection compounds base land value at the chosen rate. Illustrative
            only — not investment advice.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
